import { useEffect, useRef, useState } from 'react';

import { UIConfigData } from '../components/ColumnConfig/interface';
import { SORT_KEYS } from '../util/constants';

type SetSearchParams = (
  params: URLSearchParams,
  navigateOpts?: { replace?: boolean }
) => void;

// Reverse of SORT_KEYS: converts DB-style asc/desc back to antd's ascend/descend
const SORT_DIRECTIONS: Record<string, string> = Object.fromEntries(
  Object.entries(SORT_KEYS).map(([antdOrder, apiOrder]) => [
    apiOrder,
    antdOrder,
  ])
);

/**
 * Applies the page/pageSize/orderKey/orderValue cached on the ui-config to the
 * URL once, only when the URL doesn't already carry any of them (e.g. first visit).
 *
 * Returns `ready: true` once the URL is guaranteed to reflect the final params,
 * so callers can hold off their first data fetch until then — otherwise a fetch
 * fires with the pre-patch URL and gets immediately superseded/canceled by a
 * second fetch once the cached orderKey/orderValue are applied.
 */
export const useCachedTableParams = (
  uiConfigData: UIConfigData | undefined,
  searchParams: URLSearchParams,
  setSearchParams: SetSearchParams
) => {
  const applied = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready || !uiConfigData) return;

    // setSearchParams below was already requested on a previous run; this run
    // confirms searchParams now carries the patched values.
    if (applied.current) {
      setReady(true);
      return;
    }
    applied.current = true;

    const hasParams =
      searchParams.has('page') ||
      searchParams.has('pageSize') ||
      searchParams.has('orderKey') ||
      searchParams.has('orderValue');
    if (hasParams) {
      setReady(true);
      return;
    }

    const { orderBy } = uiConfigData;
    console.log(orderBy);
    if (!orderBy) {
      setReady(true);
      return;
    }

    let orderKey: string | undefined;
    let orderValue: string | undefined;
    try {
      const parsed = JSON.parse(orderBy) as Record<string, string>;
      console.log(parsed);

      [orderKey] = Object.keys(parsed);
      orderValue = orderKey ? SORT_DIRECTIONS[parsed[orderKey]] : undefined;
    } catch {
      setReady(true);
      return;
    }
    if (!orderKey || !orderValue) {
      setReady(true);
      return;
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set('orderKey', orderKey);
    newParams.set('orderValue', orderValue);
    setSearchParams(newParams, { replace: true });
    // ready is set to true on the next run, once searchParams reflects this update
  }, [ready, uiConfigData, searchParams, setSearchParams]);

  return ready;
};
